/**
 * How a Conversations place stays current: it loads once, reads again on every {"type":"refresh"} its socket
 * brings, once more after a reconnect so nothing is missed while the wire was down, and every 30 s when the network
 * never holds a socket (three failed connects), as the old threads did.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useOnShownAgain } from "../lib/inApp";
import { lastLoaded, load } from "../lib/kept";
import { wsQuery } from "../lib/workspace";

export type Phase = "loading" | "ready" | "error";

/** What the server says on a socket: a refresh, or someone typing (`rid` says who, `from` which side). */
export interface WireEvent {
  type?: string;
  from?: string;
  /** The guest's registration, when their socket says who they are. */
  rid?: string;
}

const TYPING_MS = 6000;
const TYPE_PING_MS = 2500;

export function useWire(path: string | null, onRefresh: () => void, onEvent?: (e: WireEvent) => void): { ping: () => void } {
  const wire = useRef<WebSocket | null>(null);
  const typedAt = useRef(0);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;
  const event = useRef(onEvent);
  event.current = onEvent;

  useEffect(() => {
    if (!path) return;
    let alive = true;
    let retry: number | undefined;
    let slow: number | undefined;
    let fails = 0;
    const connect = () => {
      if (!alive) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${proto}://${window.location.host}/api${path}${wsQuery()}`);
      wire.current = ws;
      ws.addEventListener("open", () => {
        fails = 0;
        window.clearInterval(slow);
        slow = undefined;
        refresh.current();
      });
      ws.addEventListener("message", (e) => {
        let ev: WireEvent;
        try {
          ev = JSON.parse(String(e.data));
        } catch {
          return;
        }
        if (ev.type === "refresh") refresh.current();
        else event.current?.(ev);
      });
      ws.addEventListener("close", () => {
        if (wire.current === ws) wire.current = null;
        if (!alive) return;
        fails += 1;
        if (fails >= 3 && slow === undefined) {
          slow = window.setInterval(() => document.visibilityState === "visible" && refresh.current(), 30_000);
        }
        retry = window.setTimeout(connect, 3000);
      });
    };
    connect();
    return () => {
      alive = false;
      window.clearTimeout(retry);
      window.clearInterval(slow);
      wire.current?.close();
      wire.current = null;
    };
  }, [path]);

  const ping = useCallback(() => {
    const ws = wire.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || Date.now() - typedAt.current < TYPE_PING_MS) return;
    typedAt.current = Date.now();
    try {
      ws.send(JSON.stringify({ type: "typing" }));
    } catch {
      // the wire reconnects on its own
    }
  }, []);
  return { ping };
}

/** Who is typing now; each one goes out after a few seconds of silence. Names are worked out when drawn, from what is known by then. */
export function useTypers(): { who: (nameOf: (rid: string | undefined) => string | undefined) => string[]; hear: (e: WireEvent) => void } {
  const [seen, setSeen] = useState<Record<string, number>>({});
  const timer = useRef<number | undefined>(undefined);
  const hear = useCallback((e: WireEvent) => {
    if (e.type !== "typing" || e.from === "host") return;
    setSeen((s) => ({ ...s, [e.rid ?? ""]: Date.now() }));
  }, []);
  useEffect(() => {
    window.clearTimeout(timer.current);
    if (!Object.keys(seen).length) return;
    timer.current = window.setTimeout(() => {
      const now = Date.now();
      setSeen((s) => Object.fromEntries(Object.entries(s).filter(([, at]) => now - at < TYPING_MS)));
    }, TYPING_MS);
    return () => window.clearTimeout(timer.current);
  }, [seen]);
  const who = (nameOf: (rid: string | undefined) => string | undefined) => [...new Set(Object.keys(seen).map((rid) => nameOf(rid || undefined) ?? "Someone"))];
  return { who, hear };
}

/** Tells the server how far the host has read a place: the newest line the screen showed, once per new line. */
export function useSeenUpTo(place: string | null, newest: number, done: () => void) {
  const sent = useRef(new Map<string, number>());
  const after = useRef(done);
  after.current = done;
  useEffect(() => {
    if (!place || newest <= (sent.current.get(place) ?? 0)) return;
    sent.current.set(place, newest);
    api.talk.seen(place, newest).then(() => after.current(), () => sent.current.delete(place));
  }, [place, newest]);
}

/** One place's data: kept between visits, read again on a refresh, on the phone app showing it again, and on `reload`. */
export function useRemote<T>(key: string | null, fetch: () => Promise<T>, wire: string | null = null, onEvent?: (e: WireEvent) => void) {
  const [data, setData] = useState<T | null>(() => (key ? lastLoaded<T>(key) : null));
  const [phase, setPhase] = useState<Phase>(data ? "ready" : "loading");
  const run = useRef(fetch);
  run.current = fetch;
  const current = useRef(key);
  current.current = key;

  const reload = useCallback(() => {
    const asked = key;
    if (!asked) return Promise.resolve();
    return load(asked, () => run.current())
      .then((value) => {
        if (current.current !== asked) return;
        setData(value);
        setPhase("ready");
      })
      .catch(() => {
        if (current.current === asked) setPhase((p) => (p === "ready" ? p : "error"));
      });
  }, [key]);

  useEffect(() => {
    const kept = key ? lastLoaded<T>(key) : null;
    setData(kept);
    setPhase(kept ? "ready" : "loading");
    void reload();
  }, [key, reload]);
  useOnShownAgain(() => void reload());
  const { ping } = useWire(key ? wire : null, () => void reload(), onEvent);
  return { data, phase, reload, setData, ping };
}
